import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Hex } from "@coinbase/cdp-core";
import { DeliveryStatus, DOWNLOAD_ELIGIBLE_STATUSES, FinancialStatus } from "@hybrid-escrow/shared";
import { useOrder } from "../hooks/useOrders";
import { usePublicConfig, useNetworkCheck } from "../hooks/usePublicConfig";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { useEscrowAction } from "../lib/coinbase/useEscrowAction";
import { cdpNetworkForChainId } from "../lib/coinbase/network";
import { apiClient, ApiClientError, newIdempotencyKey } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { useStartConversation } from "../hooks/useConversations";
import { Money } from "../components/Money";
import { FinancialStatusBadge, DeliveryStatusBadge } from "../components/StatusBadge";
import { StarRatingDisplay, StarRatingInput } from "../components/StarRating";
import { CountdownTimer } from "../components/CountdownTimer";
import { OrderProgressStepper } from "../components/OrderProgressStepper";
import { AccountTransferPanel } from "../components/AccountTransferPanel";
import { DomainTransferPanel } from "../components/DomainTransferPanel";
import { NftTransferPanel } from "../components/NftTransferPanel";
import { ExplorerTxLink } from "../components/ExplorerLink";
import { ErrorBanner, LoadingState, describeError } from "../components/States";
import { formatDateTime } from "../lib/format";
import { getDisputeIdForOrder } from "../lib/disputeLinks";

/** Statuses that represent "confirmed and settled/in-progress" — regressing away from one
 * of these toward an earlier state is unusual and worth calling out (e.g. a chain
 * reorganization revoking a funding event we'd already shown as confirmed). */
const ADVANCED_STATUSES: readonly string[] = [
  FinancialStatus.FUNDED,
  FinancialStatus.DISPUTED,
  FinancialStatus.RELEASED,
];

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: order, isLoading, isError, error, refetch } = useOrder(orderId);
  const { data: config } = usePublicConfig();
  const network = useNetworkCheck();
  const { principal, smartAccountAddress } = useAuthPrincipal();
  const escrowAction = useEscrowAction();
  const startConversation = useStartConversation();

  const [reorgNotice, setReorgNotice] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<{ url: string; expiresAt: string } | null>(null);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const prevStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!order) return;
    const prev = prevStatusRef.current;
    if (
      prev &&
      prev !== order.financialStatus &&
      ADVANCED_STATUSES.includes(prev) &&
      !ADVANCED_STATUSES.includes(order.financialStatus)
    ) {
      setReorgNotice(
        `This order's confirmed status changed from ${prev} back to ${order.financialStatus}. ` +
          "This can happen after a chain reorganization revokes a funding event that hadn't fully " +
          "settled. The status shown now is the authoritative, re-confirmed state.",
      );
    }
    prevStatusRef.current = order.financialStatus;
  }, [order]);

  useEffect(() => {
    if (escrowAction.phase === "confirmed" && orderId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.order(orderId) });
    }
  }, [escrowAction.phase, orderId, queryClient]);

  if (isLoading) return <LoadingState label="Loading order…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!order) return null;

  const isBuyer = principal?.walletId === order.buyerWalletId;
  const isSeller = principal?.walletId === order.sellerWalletId;
  const cdpNetwork = config ? cdpNetworkForChainId(config.chainId) : null;
  const canSignActions = Boolean(
    smartAccountAddress && cdpNetwork && network.matches && escrowAction.phase === "idle",
  );
  const deadlinePassed = order.disputeDeadline
    ? new Date(order.disputeDeadline).getTime() < Date.now()
    : false;
  const canDownload =
    isBuyer &&
    DOWNLOAD_ELIGIBLE_STATUSES.includes(order.financialStatus) &&
    order.deliveryStatus !== DeliveryStatus.REVOKED;
  const canRelease = isBuyer && order.financialStatus === FinancialStatus.FUNDED;
  const canDispute = isBuyer && order.financialStatus === FinancialStatus.FUNDED && !deadlinePassed;
  const canClaim = isSeller && order.financialStatus === FinancialStatus.FUNDED && deadlinePassed;
  const existingDisputeId = getDisputeIdForOrder(order.id);

  async function handleReconcile() {
    setReconcileError(null);
    setIsReconciling(true);
    try {
      await apiClient.reconcileOrder(order!.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.order(order!.id) });
    } catch (err) {
      setReconcileError(
        err instanceof ApiClientError && err.code === "RATE_LIMITED"
          ? "Please wait a moment before checking again."
          : describeError(err),
      );
    } finally {
      setIsReconciling(false);
    }
  }

  async function handleDownload() {
    setDownloadError(null);
    try {
      const result = await apiClient.downloadOrder(order!.id);
      setDownloadUrl(result);
      // Open immediately — the signed URL is short-lived and never logged.
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setDownloadError(describeError(err));
    }
  }

  async function handleRevealKey() {
    setDownloadError(null);
    setIsRevealing(true);
    try {
      const result = await apiClient.revealLicenseKey(order!.id);
      setRevealedKey(result.code);
    } catch (err) {
      setDownloadError(describeError(err));
    } finally {
      setIsRevealing(false);
    }
  }

  async function handleRelease() {
    if (!smartAccountAddress || !cdpNetwork || !config) return;
    await escrowAction.submit({
      smartAccountAddress,
      network: cdpNetwork,
      escrowContractAddress: config.escrowContractAddress,
      functionName: "release",
      args: [order!.orderId as Hex],
    });
  }

  async function handleClaim() {
    if (!smartAccountAddress || !cdpNetwork || !config) return;
    await escrowAction.submit({
      smartAccountAddress,
      network: cdpNetwork,
      escrowContractAddress: config.escrowContractAddress,
      functionName: "claimAfterDeadline",
      args: [order!.orderId as Hex],
    });
  }

  async function handleMessageCounterparty() {
    if (!order) return;
    setMessageError(null);
    try {
      const conversation = await startConversation.mutateAsync(order.listingId);
      navigate(`/messages/${conversation.id}`);
    } catch (err) {
      setMessageError(describeError(err));
    }
  }

  async function handleSubmitReview(e: FormEvent) {
    e.preventDefault();
    if (!order || reviewRating < 1) return;
    setReviewError(null);
    setIsSubmittingReview(true);
    try {
      await apiClient.createReview(
        order.id,
        { rating: reviewRating, comment: reviewComment.trim() || undefined },
        newIdempotencyKey(),
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.order(order.id) });
    } catch (err) {
      setReviewError(describeError(err));
    } finally {
      setIsSubmittingReview(false);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 680 }}>
      <div className="row row--between">
        <h1>Order detail</h1>
        <div className="row">
          <FinancialStatusBadge status={order.financialStatus} />
          <DeliveryStatusBadge status={order.deliveryStatus} />
        </div>
      </div>

      {/* Excludes EXPIRED/FAILED — those never entered the happy path at all, so a 4-step
          "in progress" stepper would misleadingly show step 1 as still pulsing. The status
          badge above already communicates that outcome plainly. */}
      {order.financialStatus !== FinancialStatus.EXPIRED &&
        order.financialStatus !== FinancialStatus.FAILED && (
          <div className="card">
            <OrderProgressStepper order={order} />
          </div>
        )}

      {reorgNotice && (
        <div className="alert alert--warning" role="alert">
          <strong>Status was revised.</strong>
          <p>{reorgNotice}</p>
        </div>
      )}

      {order.financialStatus === FinancialStatus.PENDING_FUNDING && (
        <div className="alert alert--info" role="status">
          Waiting for your funding transaction to confirm onchain. This page updates automatically —
          never treat a submitted transaction as funds moved until this status changes.
        </div>
      )}

      {order.listingFulfillmentType === "ACCOUNT_TRANSFER" && (
        <AccountTransferPanel
          order={order}
          isBuyer={isBuyer}
          isSeller={isSeller}
          onOrderChanged={() =>
            void queryClient.invalidateQueries({ queryKey: queryKeys.order(order.id) })
          }
        />
      )}

      {order.listingFulfillmentType === "DOMAIN_TRANSFER" && (
        <DomainTransferPanel
          order={order}
          isBuyer={isBuyer}
          isSeller={isSeller}
          onOrderChanged={() =>
            void queryClient.invalidateQueries({ queryKey: queryKeys.order(order.id) })
          }
        />
      )}

      {order.listingFulfillmentType === "NFT_TRANSFER" && (
        <NftTransferPanel
          order={order}
          isBuyer={isBuyer}
          isSeller={isSeller}
          onOrderChanged={() =>
            void queryClient.invalidateQueries({ queryKey: queryKeys.order(order.id) })
          }
        />
      )}

      <div className="card stack">
        <div className="row row--between">
          <span className="muted">Order ID</span>
          <span className="mono text-sm">{order.orderId}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Principal</span>
          <Money baseUnits={order.principalBaseUnits} />
        </div>
        <div className="row row--between">
          <span className="muted">Platform fee (nonrefundable)</span>
          <Money baseUnits={order.platformFeeBaseUnits} />
        </div>
        <div className="row row--between">
          <span className="muted">Total charged</span>
          <Money baseUnits={order.totalChargedBaseUnits} />
        </div>
        <div className="row row--between">
          <span className="muted">Placed</span>
          <span>{formatDateTime(order.createdAt)}</span>
        </div>
        {order.disputeDeadline && (
          <div className="row row--between">
            <span className="muted">Dispute deadline</span>
            <CountdownTimer deadlineIso={order.disputeDeadline} />
          </div>
        )}
        {order.fundingTransactionHash && config && (
          <div className="row row--between">
            <span className="muted">Funding tx</span>
            <ExplorerTxLink
              explorerUrl={config.explorerUrl}
              txHash={order.fundingTransactionHash}
            />
          </div>
        )}
        {order.releaseTransactionHash && config && (
          <div className="row row--between">
            <span className="muted">Release/settlement tx</span>
            <ExplorerTxLink
              explorerUrl={config.explorerUrl}
              txHash={order.releaseTransactionHash}
            />
          </div>
        )}
      </div>

      <div className="card stack">
        <h2>Actions</h2>
        <div className="row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void handleReconcile()}
            disabled={isReconciling}
          >
            {isReconciling ? "Checking…" : "Check chain state now"}
          </button>
          {canDownload && order.listingFulfillmentType === "FILE" && (
            <button type="button" className="btn btn-primary" onClick={() => void handleDownload()}>
              Download file
            </button>
          )}
          {canDownload && order.listingFulfillmentType === "LICENSE_KEY" && !revealedKey && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={isRevealing}
              onClick={() => void handleRevealKey()}
            >
              {isRevealing ? "Revealing…" : "Reveal license key"}
            </button>
          )}
          {canRelease && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canSignActions}
              onClick={() => void handleRelease()}
            >
              {escrowAction.phase === "awaiting-signature"
                ? "Confirm in wallet…"
                : "Release funds to seller"}
            </button>
          )}
          {canDispute && (
            <Link to={`/orders/${order.id}/dispute`} className="btn btn-secondary">
              Open a dispute
            </Link>
          )}
          {existingDisputeId && (
            <Link to={`/disputes/${existingDisputeId}`} className="btn btn-secondary">
              View dispute
            </Link>
          )}
          {canClaim && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canSignActions}
              onClick={() => void handleClaim()}
            >
              {escrowAction.phase === "awaiting-signature"
                ? "Confirm in wallet…"
                : "Claim funds (deadline passed)"}
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            disabled={startConversation.isPending}
            onClick={() => void handleMessageCounterparty()}
          >
            {startConversation.isPending
              ? "Opening…"
              : `Message ${isBuyer ? (order.sellerUsername ?? "seller") : (order.buyerUsername ?? "buyer")}`}
          </button>
        </div>

        {messageError && (
          <p className="field-error-text" role="alert">
            {messageError}
          </p>
        )}
        {reconcileError && (
          <p className="field-error-text" role="alert">
            {reconcileError}
          </p>
        )}
        {downloadError && (
          <p className="field-error-text" role="alert">
            {downloadError}
          </p>
        )}
        {downloadUrl && (
          <p className="text-sm muted">
            Download link opened in a new tab. It expires at {formatDateTime(downloadUrl.expiresAt)}
            .
          </p>
        )}
        {revealedKey && (
          <div className="card stack">
            <span className="muted text-sm">Your license key</span>
            <span className="mono">{revealedKey}</span>
          </div>
        )}
        {escrowAction.phase === "submitted" && (
          <p className="text-sm muted" role="status" aria-live="polite">
            Transaction submitted, waiting for onchain confirmation…
          </p>
        )}
        {escrowAction.phase === "confirmed" && (
          <p className="alert alert--success" role="status">
            Confirmed onchain. Refreshing order status…
          </p>
        )}
        {escrowAction.phase === "failed" && escrowAction.error && (
          <div className="alert alert--danger" role="alert">
            <strong>The transaction didn&apos;t go through.</strong>
            <p>{escrowAction.error}</p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={escrowAction.reset}>
              Dismiss
            </button>
          </div>
        )}
      </div>

      {isBuyer && order.financialStatus === FinancialStatus.RELEASED && (
        <div className="card stack">
          <h2>Seller review</h2>
          {order.review ? (
            <div className="stack" style={{ gap: "var(--space-1)" }}>
              <StarRatingDisplay
                rating={{ average: order.review.rating, count: 1 }}
                count={false}
              />
              {order.review.comment && <p className="text-sm">{order.review.comment}</p>}
              <p className="text-sm muted">Reviewed {formatDateTime(order.review.createdAt)}</p>
            </div>
          ) : (
            <form className="stack" onSubmit={(e) => void handleSubmitReview(e)}>
              <p className="text-sm muted">
                How was your experience with {order.sellerUsername ?? "this seller"}?
              </p>
              <StarRatingInput value={reviewRating} onChange={setReviewRating} />
              <div className="field">
                <label htmlFor="review-comment">Comment (optional)</label>
                <textarea
                  id="review-comment"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  maxLength={1000}
                  rows={3}
                />
              </div>
              {reviewError && (
                <p className="field-error-text" role="alert">
                  {reviewError}
                </p>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={reviewRating < 1 || isSubmittingReview}
              >
                {isSubmittingReview ? "Submitting…" : "Submit review"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
